import React, { useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { StickyContainer, Sticky } from 'react-sticky';
import ReactGA from 'react-ga';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import {
  fetchProduct,
  setProduct,
  clearProduct,
  addProduct,
} from '../store/actions/index';

import Image from '../components/Image';
import ProductsLoading from '../components/ProductsLoading';

import { titles } from '../utils/seoData';
import { formatNumber } from '../utils/format';
import { matchProps } from '../utils/propTypes';

const Products = (props) => {
  const product = useSelector(state => state.product.product);
  const dispatch = useDispatch();

  useEffect(() => {
    const { token, url_title } = props.match.params;
    if (window.PRODUCT && window.PRODUCT.hasOwnProperty('token') && window.PRODUCT.token === token) {
      dispatch(setProduct(window.PRODUCT));
    } else {
      dispatch(fetchProduct(token, url_title));
    }

    return () => {
      dispatch(clearProduct());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.match.params.token]);

  useEffect(() => {
    if (product.name) {
      document.title = titles('products', `${product.ITEM_NAME} - ${product.DESCRIPTION}`).title;
    }
  }, [product]);

  // No product yet?
  if (!product.ITEM_NAME) {
    return (
      <ProductsLoading />
    );
  }

  const renderMobileInfo = (style, isSticky) => {
    const classNames = isSticky ? 'show-for-mobile sticky sticky__product' : 'show-for-mobile';

    return (
      <div className="mobile-info">
        <div className={classNames} style={style}>
          <div className="mobile-product-header">
            <div className="mobile-product-title">
              <div className="product-title">{product.ITEM_NAME}</div>
              <div>
                {renderPrice()}
              </div>
            </div>
            {renderButton('Buy', false)}
          </div>
        </div>
      </div>
    );
  }

  const submit = () => {
    const price = formatNumber(product.PRICE);

    dispatch(addProduct({ TOKEN: product.TOKEN, price, name: product.ITEM_NAME }));
    ReactGA.plugin.execute('ecommerce', 'addItem', {
      // Provide product details in an impressionFieldObject.
      'id': product.TOKEN, // Product ID (string).
      'name': product.ITEM_NAME, // Product name (string).
      'brand': product.DESCRIPTION, // Product brand (string).
      'price': price,
      'quantity': 1,
    });
    ReactGA.plugin.execute('ecommerce', 'send');
    ReactGA.plugin.execute('ecommerce', 'clear');
  }

  const renderButton = (text, showStock = true) => {
    if (product.CURRENT_QUANTITY > 0) {
      return (
        <div>
          {showStock && <p>Store availability: in stock!</p>}
          <p>
            <Button
              type="submit"
              className="cw-button"
              onClick={submit}
              variant="contained"
              color="primary"
            >
              {text}
            </Button>
          </p>
        </div>
      );
    }
  }

  const renderReminder = () => {
    if (product.CURRENT_QUANTITY > 0) {
      return null;
    }

    return (
      <>
        <p>Store availability: out of stock</p>
        <div className="product--outofstock">
          <h4>Get notified on future items.</h4>
          <p><small>Sign up for our newsletter, or follow us on social media. Our weekly <Link to="/checklist">comic book subscription service</Link> and <Link to="/final-order-cutoff">preorder discounts</Link> are the best way to reserve your copy on upcoming comics and toys <strong>at a great discount!</strong>.</small></p>
        </div>
      </>
    );
  }

  const renderNormalBanner = () => {
    return (
      <div className="product--banner">
        <div className="background" />
        <div className="background-gradient" />
        <div className="banner--container" />
      </div>
    );
  }

  const renderPrice = () => {
    const price = `$${formatNumber(product.PRICE)}`

    return (
      <p>
        <strong>Price:</strong> {price}
      </p>
    );
  }

  return (
    <StickyContainer>
      <Sticky className="mobile-info">{({ style, isSticky }) => (
        <div>{renderMobileInfo(style, isSticky)}</div>
        )}
      </Sticky>
      {renderNormalBanner()}
      <div className="product--container">
        <article className="product--data">
          <div className="product--image">
            <Image
              imgSmall={product.filename_small}
              imgLarge={product.filename_large}
              name={product.ITEM_NAME}
              imgClassName=""
            />
          </div>
          <div className="product--content">
            <div className="product-description">
              <div className="product--heading__container">
                <div className="product--heading">
                  {product.DESCRIPTION}
                  <h1 className="product--heading__title">{product.ITEM_NAME}</h1>
                </div>
                <div className="product--cta">
                  {renderPrice()}
                  {renderButton('Add to Cart')}
                  {renderReminder()}
                </div>
              </div>

              <div dangerouslySetInnerHTML={{__html: product.description}} />
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Product code: {product.VENDOR_CODE} UPC: {product.SKU}
              </Typography>
            </div>
          </div>
        </article>
      </div>
    </StickyContainer>
  );
}

Products.propTypes = {
  clearProduct: PropTypes.func,
  fetchProduct: PropTypes.func,
  match: matchProps,
  params: PropTypes.object,
  product: PropTypes.object,
  addProduct: PropTypes.func,
  tag: PropTypes.object,
};

export default Products;
